import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { CheckCircleIcon, ClockIcon, BanknotesIcon, ArrowRightIcon } from '@heroicons/react/24/outline'

interface PaymentSlipSuccessState {
  invoiceId: string
  amount: number
  fileName: string
  returnPath?: string
}

export const PaymentSlipSuccessPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as PaymentSlipSuccessState

  // If no state provided, redirect back
  useEffect(() => {
    if (!state?.invoiceId) {
      navigate('/payment', { replace: true })
    }
  }, [state, navigate])

  if (!state?.invoiceId) {
    return null // Will redirect in useEffect
  }

  const formatThaiBaht = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const handleReturnToPayments = () => {
    if (state.returnPath) {
      navigate(state.returnPath)
    } else {
      navigate('/payment')
    }
  }

  const handleViewPaymentHistory = () => {
    navigate('/payment')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="อัพโหลดสำเร็จ"
        showBackButton={false}
      />
      
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircleIcon className="h-10 w-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            ส่งสลิปสำเร็จ!
          </h1>
          <p className="text-gray-600">
            ได้รับสลิปการโอนของคุณแล้ว
          </p>
        </div>

        {/* Payment Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">รายละเอียดการส่งสลิป</h2>
          
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ใบแจ้งหนี้:</span>
              <span className="font-medium">{state.invoiceId}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">จำนวนเงิน:</span>
              <span className="font-medium text-blue-600">{formatThaiBaht(state.amount)}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">ไฟล์ที่ส่ง:</span>
              <span className="font-medium text-sm text-gray-800 truncate max-w-48">{state.fileName}</span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">เวลาที่ส่ง:</span>
              <span className="font-medium">
                {new Date().toLocaleString('th-TH', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
        </div>

        {/* Status Information */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <ClockIcon className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-yellow-800 mb-1">รอการตรวจสอบ</h3>
              <p className="text-sm text-yellow-700 mb-2">
                ทางโรงเรียนจะตรวจสอบสลิปการโอนของคุณและแจ้งผลภายใน 2-4 ชั่วโมง 
                (ในเวลาทำการ)
              </p>
              <p className="text-xs text-yellow-600">
                💡 คุณจะได้รับการแจ้งเตือนผ่าน LINE เมื่อการตรวจสอบเสร็จสิ้น
              </p>
            </div>
          </div>
        </div>

        {/* What Happens Next */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <h3 className="font-medium text-blue-900 mb-3">📋 ขั้นตอนต่อไป</h3>
          <div className="space-y-2 text-sm text-blue-800">
            <div className="flex items-center">
              <span className="w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0">1</span>
              <span>ทางโรงเรียนตรวจสอบข้อมูลในสลิป</span>
            </div>
            <div className="flex items-center">
              <span className="w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0">2</span>
              <span>ยืนยันการชำระเงินในระบบ</span>
            </div>
            <div className="flex items-center">
              <span className="w-6 h-6 bg-blue-600 text-white text-xs rounded-full flex items-center justify-center mr-3 flex-shrink-0">3</span>
              <span>ส่งการแจ้งเตือนยืนยันผ่าน LINE</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleViewPaymentHistory}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <BanknotesIcon className="h-5 w-5 mr-2" />
            ดูประวัติการชำระเงิน
          </button>
          
          <button
            onClick={handleReturnToPayments}
            className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
          >
            <ArrowRightIcon className="h-5 w-5 mr-2 rotate-180" />
            กลับไปหน้าการชำระเงิน
          </button>
        </div>

        {/* Contact Information */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-500 mb-2">
            หากมีข้อสงสัยหรือต้องการความช่วยเหลือ
          </p>
          <p className="text-sm font-medium text-gray-700">
            📞 โทร: 081-XXX-XXXX
          </p>
          <p className="text-xs text-gray-500 mt-1">
            เวลาทำการ: จันทร์-เสาร์ 08:00-17:00 น.
          </p>
        </div>
      </div>
    </div>
  )
}